import { useEffect, useState, type FormEvent } from 'react';
import type { BusinessOpportunity, BusinessOpportunityStatus } from '../types';
import { GlobalButton } from './GlobalButton';
import { GlobalInput } from './GlobalInput';
import { GlobalTextarea } from './GlobalTextarea';
import { GlobalDropdown } from './GlobalDropdown';
import { newId } from '../utils/id';

type Props = {
  opportunity?: BusinessOpportunity | null;
  onSubmit: (next: BusinessOpportunity) => void;
  onCancel: () => void;
};

type OpportunityDraft = {
  name: string;
  industry: string;
  ideaSummary: string;
  status: BusinessOpportunityStatus;
};

const STATUS_OPTIONS: Array<{ value: BusinessOpportunityStatus; label: string }> = [
  { value: 'draft', label: 'Draft' },
  { value: 'refining', label: 'Refining' },
  { value: 'validated', label: 'Validated' },
  { value: 'archived', label: 'Archived' },
];

function emptyDraft(): OpportunityDraft {
  return {
    name: '',
    industry: '',
    ideaSummary: '',
    status: 'draft',
  };
}

function draftFromOpportunity(opportunity: BusinessOpportunity): OpportunityDraft {
  return {
    name: opportunity.name,
    industry: opportunity.industry ?? '',
    ideaSummary: opportunity.ideaSummary,
    status: opportunity.status,
  };
}

export function BusinessOpportunityForm({ opportunity, onSubmit, onCancel }: Props) {
  const isEdit = Boolean(opportunity);
  const [draft, setDraft] = useState<OpportunityDraft>(
    opportunity ? draftFromOpportunity(opportunity) : emptyDraft()
  );

  useEffect(() => {
    setDraft(opportunity ? draftFromOpportunity(opportunity) : emptyDraft());
  }, [opportunity]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const now = new Date().toISOString();
    const name = draft.name.trim();
    const ideaSummary = draft.ideaSummary.trim();
    if (!name || !ideaSummary) return;

    onSubmit({
      id: opportunity?.id ?? newId(),
      name,
      industry: draft.industry.trim() || undefined,
      ideaSummary,
      status: draft.status,
      ideaIntelligence: opportunity?.ideaIntelligence,
      refinements: opportunity?.refinements,
      feedback: opportunity?.feedback,
      pnl: opportunity?.pnl,
      promotedCaseId: opportunity?.promotedCaseId,
      createdAt: opportunity?.createdAt ?? now,
      updatedAt: now,
    });
  }

  return (
    <form className="form-stack" onSubmit={handleSubmit}>
      <GlobalInput
        label="Name"
        value={draft.name}
        onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
        placeholder="e.g. AI bookkeeping for freelancers"
        required
      />
      <GlobalInput
        label="Industry"
        value={draft.industry}
        onChange={(e) => setDraft((prev) => ({ ...prev, industry: e.target.value }))}
        placeholder="e.g. FinTech, SaaS"
      />
      {isEdit ? (
        <GlobalDropdown
          label="Status"
          value={draft.status}
          onChange={(value) =>
            setDraft((prev) => ({ ...prev, status: value as BusinessOpportunityStatus }))
          }
          options={STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        />
      ) : null}
      <GlobalTextarea
        label="Idea summary"
        value={draft.ideaSummary}
        onChange={(e) => setDraft((prev) => ({ ...prev, ideaSummary: e.target.value }))}
        placeholder="Describe the problem, solution, target customer, and why now. At least 100 characters for analysis."
        rows={10}
        required
      />
      <div className="form-actions">
        <GlobalButton type="submit" variant="primary">
          {isEdit ? 'Save' : 'Add opportunity'}
        </GlobalButton>
        <GlobalButton type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </GlobalButton>
      </div>
    </form>
  );
}
